import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface Opcion {
  texto: string;
  valor: string;
}

interface Pregunta {
  texto: string;
  opciones: Opcion[];
}

interface ResultadoData {
  titulo: string;
  subtitulo: string;
  precio: string;
  descripcion: string;
  incluye: string[];
  link: string;
  color: 'green' | 'terracotta' | 'brown';
}

const LETRAS = ['A', 'B', 'C', 'D'];

const preguntas: Pregunta[] = [
  {
    texto: '¿Cuántos meses tiene tu bebé?',
    opciones: [
      { texto: '0 a 3 meses — recién nacido', valor: 'newborn' },
      { texto: '3 a 6 meses', valor: '3to6' },
      { texto: '6 a 12 meses', valor: '6to12' },
      { texto: '12 a 24 meses', valor: '12to24' },
    ],
  },
  {
    texto: '¿Cuántas veces se despierta por noche, aproximadamente?',
    opciones: [
      { texto: '1 a 2 veces — puedo manejarlo', valor: 'poco' },
      { texto: '3 a 5 veces — estoy cansada pero aguanto', valor: 'medio' },
      { texto: 'Más de 5 veces o cada hora — ya no doy más', valor: 'mucho' },
      { texto: 'No sé, perdí la cuenta hace semanas', valor: 'mucho' },
    ],
  },
  {
    texto: '¿Cómo logra dormirse tu bebé en este momento?',
    opciones: [
      { texto: 'Con teta o mamadera en la boca', valor: 'asociacion_teta' },
      { texto: 'En brazos o en movimiento', valor: 'asociacion_brazos' },
      { texto: 'Con chupete o con ruido blanco', valor: 'asociacion_otros' },
      { texto: 'Solo, en su cuna, sin ayuda', valor: 'autonomo' },
    ],
  },
  {
    texto: '¿Qué describe mejor lo que estás viviendo?',
    opciones: [
      { texto: 'No entiendo por qué se despierta si lo atendí bien durante el día', valor: 'confundida' },
      { texto: 'Dormía mejor y de golpe empeoró sin razón aparente', valor: 'regresion' },
      { texto: 'Nunca durmió bien, desde el principio fue difícil', valor: 'siempre' },
      { texto: 'Estoy al límite, ya no puedo más con el cansancio', valor: 'agotada' },
    ],
  },
  {
    texto: '¿Qué rol tiene el yoga, la respiración o el autocuidado en tu vida ahora mismo?',
    opciones: [
      { texto: 'Nada, no tengo tiempo ni energía para nada de eso', valor: 'cero' },
      { texto: 'Me gustaría incorporarlo pero no sé cómo', valor: 'quiere' },
      { texto: 'Practico algo, aunque sea poco', valor: 'algo' },
      { texto: 'Es importante para mí y quiero que forme parte del plan', valor: 'mucho_yoga' },
    ],
  },
  {
    texto: '¿Cuánto sabés sobre la neurociencia del sueño infantil?',
    opciones: [
      { texto: 'Casi nada — por eso estoy acá', valor: 'poco_saber' },
      { texto: 'Algunas cosas, pero me confundo con tanta información contradictoria', valor: 'medio_saber' },
      { texto: 'Bastante, pero igual los métodos no me funcionan', valor: 'mucho_saber' },
      { texto: 'Quiero entender el por qué, no solo el cómo', valor: 'quiere_saber' },
    ],
  },
  {
    texto: '¿Cómo te sentís vos ahora mismo?',
    opciones: [
      { texto: 'Cansada pero estable, busco optimizar', valor: 'estable' },
      { texto: 'Agotada, con culpa y sin saber si lo estoy haciendo bien', valor: 'culpa' },
      { texto: 'Al límite, con poca paciencia y necesito un cambio ya', valor: 'limite' },
      { texto: 'Bien, pero quiero entender mejor para acompañarla/lo', valor: 'bien' },
    ],
  },
];

const resultados: Record<string, ResultadoData> = {
  fundamentos: {
    titulo: 'Fundamentos del Sueño',
    subtitulo: 'Tu bebé necesita que entiendas qué le pasa — antes de aplicar cualquier técnica.',
    precio: '$27 USD',
    descripcion: 'El sueño de tu bebé tiene una lógica neurológica que muy pocos explican con claridad. Una vez que la entendés, todo cambia. Este programa cubre los ciclos de sueño, las señales de cansancio, la ventana de sueño y cómo crear el ambiente y la rutina que realmente funcionan — basado en ciencia, sin métodos que impliquen llorar.',
    incluye: ['8 guías en PDF con neurociencia aplicada', 'Principios de yoga para acompañar cada etapa', 'Para bebés de 0 a 6 meses', 'Acceso de por vida'],
    link: '/fundamentos-sueno',
    color: 'terracotta',
  },
  yoga: {
    titulo: 'Yoga Sueño Profundo',
    subtitulo: 'Tu bebé ya pasó la etapa inicial. Lo que necesita ahora es movimiento, vínculo y regulación.',
    precio: '$67 USD',
    descripcion: 'Cuando un bebé no logra enlazar sus ciclos de sueño y necesita volver a dormirse con ayuda, el cuerpo tiene mucho que decir. Las secuencias de yoga mamá-bebé calman el sistema nervioso de los dos, construyen el vínculo que también ayuda a dormir, y preparan el cuerpo para el descanso profundo.',
    incluye: ['14 guías: los 8 fundamentos + 6 de yoga mamá-bebé', 'Secuencias para 0-3, 3-6 y 6-12 meses', 'Regulación emocional para mamá', 'Ritual nocturno completo'],
    link: '/yoga-sueno-profundo',
    color: 'brown',
  },
  completo: {
    titulo: 'Programa Completo NeuroBabys',
    subtitulo: 'Necesitás el plan completo: neurociencia, yoga y herramientas para cada etapa hasta los 24 meses.',
    precio: '$147 USD',
    descripcion: 'Para cuando el cansancio ya llegó al límite y necesitás algo que realmente funcione de punta a punta. El Programa Completo incluye todo lo de los niveles anteriores más las guías de regresiones, despertares nocturnos, vínculo y nuevos desafíos del segundo año — y tres álbumes de música diseñada para el sueño infantil.',
    incluye: ['20 guías + 2 bonus + 3 álbumes musicales', 'Guía de emergencia nocturna (para las 3am)', 'Diario de sueño NeuroBaby (28 días)', 'Para bebés de 0 a 24 meses'],
    link: '/programa-completo-neurobabys',
    color: 'green',
  },
};

function calcularResultado(vals: string[]): string {
  const agotada = vals.includes('agotada') || vals.includes('limite') || vals.includes('mucho');
  const yoga = vals.includes('mucho_yoga') || vals.includes('algo') || vals.includes('quiere');
  const edad = vals[0];

  if (edad === '12to24' || (yoga && agotada)) {
    return 'completo';
  }
  if (yoga && !agotada) {
    return 'yoga';
  }
  return 'fundamentos';
}

export default function TestSueno() {
  const { t } = useTranslation();
  const [screen, setScreen] = useState<'intro' | 'question' | 'email' | 'result'>('intro');
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMsg, setEmailErrorMsg] = useState('');
  const [resultKey, setResultKey] = useState<string>('');
  const [animKey, setAnimKey] = useState(0);

  const totalPasos = preguntas.length + 2;

  const getProgress = useCallback(() => {
    if (screen === 'intro') return 0;
    if (screen === 'question') return ((preguntaActual) / totalPasos) * 100;
    if (screen === 'email') return ((preguntas.length + 1) / totalPasos) * 100;
    return 100;
  }, [screen, preguntaActual, totalPasos]);

  const startQuiz = () => {
    setScreen('question');
    setAnimKey((k) => k + 1);
  };

  const elegir = (valor: string) => {
    const nuevasRespuestas = [...respuestas];
    nuevasRespuestas[preguntaActual] = valor;
    setRespuestas(nuevasRespuestas);

    setTimeout(() => {
      if (preguntaActual + 1 < preguntas.length) {
        setPreguntaActual((p) => p + 1);
        setAnimKey((k) => k + 1);
      } else {
        setScreen('email');
        setAnimKey((k) => k + 1);
      }
    }, 320);
  };

  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const showResult = () => {
    if (!email.trim()) {
      setEmailError(true);
      setEmailErrorMsg(t('test_sueno.email.error_empty', 'Por favor, ingresá tu email.'));
      return;
    }
    if (!isValidEmail(email.trim())) {
      setEmailError(true);
      setEmailErrorMsg(t('test_sueno.email.error_invalid', 'Ingresá un email válido (ej: tu@email.com).'));
      return;
    }
    setEmailError(false);
    setEmailErrorMsg('');
    const clave = calcularResultado(respuestas);
    setResultKey(clave);
    setScreen('result');
    setAnimKey((k) => k + 1);
  };

  const reiniciar = () => {
    setPreguntaActual(0);
    setRespuestas([]);
    setEmail('');
    setEmailError(false);
    setResultKey('');
    setScreen('intro');
    setAnimKey((k) => k + 1);
  };

  const r = resultKey ? resultados[resultKey] : null;

  const btnColorClass =
    r?.color === 'green'
      ? 'bg-[#2D5016] hover:bg-[#1F3A0E]'
      : r?.color === 'brown'
        ? 'bg-[#8B6F5C] hover:bg-[#6B5B4A]'
        : 'bg-terracotta hover:bg-[#B07A62]';

  return (
    <div className="min-h-screen bg-[#F7F2EA]">
      <Navbar />

      {/* Progress bar */}
      <div className="w-full h-[3px] bg-[#EDE4D6]">
        <div
          className="h-[3px] bg-terracotta transition-all duration-500 ease-out"
          style={{ width: `${getProgress()}%` }}
        />
      </div>

      <div className="max-w-[600px] mx-auto px-6 py-10 md:py-16 pb-20">
        {/* INTRO */}
        {screen === 'intro' && (
          <div
            key={`intro-${animKey}`}
            className="text-center animate-[fadeIn_0.35s_ease]"
          >
            <div className="text-[11px] tracking-[2.5px] uppercase text-terracotta mb-4">
              {t('test_sueno.intro.eyebrow', 'Test gratuito · 2 minutos')}
            </div>
            <h1 className="font-serif text-[clamp(1.875rem,5vw,2.625rem)] font-bold text-[#2D5016] leading-[1.15] mb-4">
              {t('test_sueno.intro.title', '¿Qué está pasando con el sueño de tu bebé?')}
            </h1>
            <div className="text-xl text-[#C9A84C] my-5">
              <i className="ri-moon-line" />
            </div>
            <p className="text-[15px] leading-[1.7] text-[#6B5B4A] max-w-[460px] mx-auto mb-8">
              {t('test_sueno.intro.desc', 'Respondé 7 preguntas y te decimos exactamente qué está ocurriendo y qué necesitás para que las noches mejoren de verdad.')}
            </p>
            <p className="text-sm mb-7 text-[#6B5B4A]">
              {t('test_sueno.intro.author', 'Creado por Juan Cruz ·')} <strong className="text-[#2D5016]">NeuroBabys</strong>
            </p>
            <button
              onClick={startQuiz}
              className="w-full py-[18px] px-6 bg-[#2D5016] text-[#FDFAF6] rounded-[10px] font-sans text-[15px] font-bold tracking-[0.5px] hover:bg-[#1F3A0E] hover:-translate-y-[1px] transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('test_sueno.intro.start', 'Empezar el test →')}
            </button>
            <p className="text-xs mt-4 text-[#B0A090]">
              {t('test_sueno.intro.footer', 'Sin datos médicos. Sin registro previo. 100% gratis.')}
            </p>
          </div>
        )}

        {/* QUESTION */}
        {screen === 'question' && (
          <div
            key={`q-${preguntaActual}-${animKey}`}
            className="animate-[fadeIn_0.35s_ease]"
          >
            <div className="text-[11px] tracking-[2px] uppercase text-terracotta mb-3">
              {t('test_sueno.question.counter', 'Pregunta {{num}} de {{total}}', { num: preguntaActual + 1, total: preguntas.length })}
            </div>
            <h2 className="font-serif text-[clamp(1.375rem,4.5vw,1.875rem)] font-bold text-[#2D5016] leading-[1.25] mb-7">
              {preguntas[preguntaActual].texto}
            </h2>
            <div className="flex flex-col gap-3">
              {preguntas[preguntaActual].opciones.map((op, i) => (
                <button
                  key={op.valor}
                  onClick={() => elegir(op.valor)}
                  className="text-left bg-[#FDFAF6] border border-[#EDE4D6] rounded-[10px] px-5 py-4 text-[15px] leading-[1.5] text-[#6B5B4A] transition-all duration-200 hover:border-terracotta hover:bg-[#FAF5EF] hover:-translate-y-[1px] cursor-pointer flex items-start gap-3"
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#EDE4D6] text-terracotta font-bold text-xs flex-shrink-0 mt-0.5">
                    {LETRAS[i]}
                  </span>
                  <span>{op.texto}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* EMAIL */}
        {screen === 'email' && (
          <div
            key={`email-${animKey}`}
            className="text-center animate-[fadeIn_0.35s_ease]"
          >
            <div className="text-xl text-[#C9A84C] mb-4">
              <i className="ri-sparkling-line" />
            </div>
            <h2 className="font-serif text-[28px] font-bold text-[#2D5016] mb-3">
              {t('test_sueno.email.title', 'Ya tenemos tu diagnóstico')}
            </h2>
            <p className="text-[15px] leading-[1.7] text-[#6B5B4A] mb-7">
              {t('test_sueno.email.desc', 'Dejanos tu email y te enviamos tu diagnóstico personalizado — más el capítulo Despertares nocturnos sin culpa, completamente gratis. Tu regalo para sobrevivir esta noche.')}
            </p>
            <input
              type="text"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setEmailError(false); setEmailErrorMsg(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') showResult(); }}
              placeholder={t('test_sueno.email.placeholder', 'tu@email.com')}
              className={`w-full px-5 py-4 border rounded-[10px] text-base font-sans bg-[#FDFAF6] text-[#6B5B4A] outline-none transition-colors duration-200 ${emailError ? 'border-terracotta' : 'border-[#EDE4D6] focus:border-terracotta'}`}
            />
            {emailError && (
              <p className="text-sm text-terracotta mb-3 text-left">
                {emailErrorMsg}
              </p>
            )}
            <button
              onClick={showResult}
              className="w-full py-[18px] px-6 bg-[#2D5016] text-[#FDFAF6] rounded-[10px] font-sans text-[15px] font-bold tracking-[0.5px] hover:bg-[#1F3A0E] hover:-translate-y-[1px] transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('test_sueno.email.cta', 'Ver mi diagnóstico →')}
            </button>
            <p className="text-xs mt-3 text-[#6B5B4A]">
              <i className="ri-information-line mr-1" />
              {t('test_sueno.email.hint', 'Necesitás completar tu email para ver tu diagnóstico online')}
            </p>
            <p className="text-xs mt-2 text-[#B0A090]">
              {t('test_sueno.email.privacy', 'Sin spam. Podés darte de baja en cualquier momento.')}
            </p>
          </div>
        )}

        {/* RESULT */}
        {screen === 'result' && r && (
          <div
            key={`result-${animKey}`}
            className="animate-[fadeIn_0.35s_ease]"
          >
            {/* Badge */}
            <div className="bg-[#2D5016] text-[#F7F2EA] rounded-xl px-6 py-7 text-center mb-7">
              <div className="text-[10px] tracking-[2.5px] uppercase text-[#C9A84C] mb-2">
                {t('test_sueno.result.eyebrow', 'Tu diagnóstico personalizado')}
              </div>
              <h2 className="font-serif text-[28px] font-bold text-[#F7F2EA] leading-[1.2] mb-2">
                {r.titulo}
              </h2>
              <p className="text-sm leading-[1.6] text-[#C8BCA8]">
                {r.subtitulo}
              </p>
            </div>

            {/* Explanation */}
            <div className="bg-[#FDFAF6] rounded-xl p-6 mb-5 border-l-[3px] border-terracotta">
              <h3 className="font-serif text-xl text-[#2D5016] mb-2">
                {t('test_sueno.result.explanation_title', 'Qué está pasando y qué necesitás')}
              </h3>
              <p className="text-sm leading-[1.7] text-[#6B5B4A]">
                {r.descripcion}
              </p>
            </div>

            {/* Includes */}
            <div className="bg-[#FDFAF6] rounded-xl p-6 mb-5 border-l-[3px] border-terracotta">
              <h3 className="font-serif text-xl text-[#2D5016] mb-3">
                {t('test_sueno.result.includes_title', 'Qué incluye')}
              </h3>
              {r.incluye.map((item) => (
                <p key={item} className="text-sm text-[#6B5B4A] mb-1.5 flex items-start gap-2">
                  <span className="text-terracotta flex-shrink-0">✓</span>
                  <span>{item}</span>
                </p>
              ))}
              <p className="mt-4 text-sm font-bold text-[#2D5016]">
                {t('test_sueno.result.price_label', 'Precio:')} {r.precio} · {t('test_sueno.result.access', 'Acceso inmediato · Pago único')}
              </p>
            </div>

            {/* Bonus banner */}
            <div className="bg-[#FBF0DC] border border-[#C9A84C] rounded-xl px-6 py-5 mb-7 text-center">
              <p className="text-sm leading-[1.7] text-[#6B5B4A]">
                <span className="text-lg">🎁</span>{' '}
                <strong className="text-[#2D5016]">{t('test_sueno.result.gift_title', 'Regalo para esta noche:')}</strong>{' '}
                {t('test_sueno.result.gift_desc', 'Te enviamos gratis la Guía de Emergencia Nocturna — qué hacer en los próximos 90 segundos cuando todo se desarmó y no podés pensar. A tu email ahora.')}
              </p>
            </div>

            {/* CTA buttons */}
            <Link
              to={r.link}
              className={`block w-full py-[18px] px-6 text-[#FDFAF6] rounded-[10px] font-sans text-[15px] font-bold tracking-[0.5px] hover:-translate-y-[1px] transition-all duration-200 text-center whitespace-nowrap ${btnColorClass}`}
            >
              {t('test_sueno.result.cta', 'Quiero este programa →')}
            </Link>
            <button
              onClick={reiniciar}
              className="w-full py-[18px] px-6 bg-transparent border border-terracotta text-terracotta rounded-[10px] font-sans text-[15px] font-bold tracking-[0.5px] hover:bg-[#FAF5EF] hover:-translate-y-[1px] transition-all duration-200 mt-3 cursor-pointer"
            >
              {t('test_sueno.result.restart', 'Volver a hacer el test')}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Footer />
    </div>
  );
}